import {
  ActionIcon,
  Alert,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useListState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconDeviceFloppy,
  IconDownload,
  IconPlus,
  IconPrinter,
  IconProgressCheck,
  IconX,
} from "@tabler/icons-react";
import { logError } from "api/diagnostic.service";
import { pickFields, toCsv } from "common/csv";
import { navigateTo } from "common/navigation";
import React, { startTransition, useEffect, useRef, useState } from "react";
import { IDataEntryFormProps } from "types/interface.types";
import {
  SupplierOrderDataEntryForm,
  SupplierOrderEntry,
} from "types/model.types";
import { OrderEntryRow } from "./AdminSupplierOrderEntryRow";

import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_INVALID,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
} from "common/constants";
import {
  confirmReset,
  formatDate,
  formatNumberAsCurrencyString,
  getLocalDateString,
} from "common/utils";

import { Select, Text } from "@mantine/core";
import { safeApiCall } from "api/common.service";
import {
  calculateSupplierOrderPricing,
  deleteSupplierOrderDetailsById,
  getSupplierOrderById,
  getSuppliers,
  orderReceived,
  resetSupplierOrderDetails,
  submitSupplierOrder,
  togglePrintState,
} from "api/supplier.service";
import { Virtuoso } from "react-virtuoso";
import { useProductStore } from "stores/useProductStore";
import AdminSupplierOrderPreview from "./AdminSupplierOrderPreview";

interface IOrderEntryHeaderRowProps {
  orderReceived: boolean;
  onCommChange: (value: number | null) => void;
  onPr1Change: (value: number | null) => void;
}
const OrderEntryHeaderRow = ({
  orderReceived,
  onCommChange,
  onPr1Change,
}: IOrderEntryHeaderRowProps) => {
  const [commValue, setCommValue] = useState<number | null>(null);
  const [pr1Value, setPr1Value] = useState<number | null>(null);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: orderReceived
          ? "0.5fr 1.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr"
          : "0.5fr 3.5fr 0.5fr 0.5fr 0.5fr 0.5fr",
        gap: "8px",
        marginBottom: "8px",
        width: "100%",
        alignItems: "center", // 👈 ensures inputs and text align vertically
      }}
    >
      <Text ta={"left"} fw={600} size="md">
        Product #
      </Text>
      <Text
        size="md"
        fw={600}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        Description
      </Text>
      <Text ta={"left"} fw={600} size="md">
        Ordered
      </Text>
      <Text ta={"left"} fw={600} size="md">
        Supplied
      </Text>
      {orderReceived ? (
        <>
          <Stack>
            <Text ta={"left"} fw={600} size="md">
              Comm
            </Text>
            <Group gap="xs">
              <NumberInput
                value={commValue ?? ""}
                onChange={(val) =>
                  setCommValue(val === "" ? null : Number(val))
                }
                decimalScale={0}
                min={0}
                max={999}
                maxLength={3}
                size="xs"
                hideControls
                styles={{
                  input: {
                    width: "35px", // ← make it as tiny as you want
                    paddingRight: 4, // optional: tighten spacing
                    paddingLeft: 4,
                  },
                }}
              />

              <ActionIcon
                color="green"
                variant="light"
                size="sm"
                onClick={() => onCommChange(commValue)}
              >
                <IconCheck size={16} />
              </ActionIcon>

              <ActionIcon
                color="red"
                variant="light"
                size="sm"
                onClick={() => {
                  setCommValue(null);
                  onCommChange(null);
                }}
              >
                <IconX size={16} />
              </ActionIcon>
            </Group>
          </Stack>

          <Stack>
            <Text ta={"left"} fw={600} size="md">
              PR %1
            </Text>
            <Group gap="xs">
              <NumberInput
                value={pr1Value ?? ""}
                onChange={(val) => setPr1Value(val === "" ? null : Number(val))}
                min={0}
                max={999}
                maxLength={4}
                size="xs"
                hideControls
                styles={{
                  input: {
                    width: "35px", // ← make it as tiny as you want
                    paddingRight: 4, // optional: tighten spacing
                    paddingLeft: 4,
                  },
                }}
              />

              <ActionIcon
                color="green"
                variant="light"
                size="sm"
                onClick={() => onPr1Change(pr1Value)}
              >
                <IconCheck size={16} />
              </ActionIcon>

              <ActionIcon
                color="red"
                variant="light"
                size="sm"
                onClick={() => {
                  setPr1Value(null);
                  onPr1Change(null);
                }}
              >
                <IconX size={16} />
              </ActionIcon>
            </Group>
          </Stack>
          <Text ta={"left"} fw={600} size="md">
            Cur. Cost
          </Text>
          <Text ta={"left"} fw={600} size="md">
            Cost
          </Text>
          <Text ta={"left"} fw={600} size="md">
            Cur. SP #1
          </Text>
          <Text ta={"left"} fw={600} size="md">
            New SP #1
          </Text>
          <Text ta={"left"} fw={600} size="md">
            Price Change
          </Text>
          <Text ta={"left"} fw={600} size="md">
            Cur. SP #2
          </Text>
          <Text ta={"left"} fw={600} size="md">
            New SP #2
          </Text>
          <Text ta={"left"} fw={600} size="md">
            Best Before
          </Text>
        </>
      ) : (
        <Text ta={"left"} fw={600} size="md">
          Barcode
        </Text>
      )}

      <Text ta={"left"} fw={600} size="md">
        Actions
      </Text>
    </div>
  );
};
export const AdminSupplierOrderEntryPage: React.FC<IDataEntryFormProps> = ({
  recordId,
  onSaveComplete,
}) => {
  const apiPostRef = useRef(false);
  const allProducts = useProductStore((s) => s.allProducts);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<
    { value: string; label: string }[]
  >([]);

  const [entries, handlers] = useListState<SupplierOrderEntry>([]);
  const [preview, setPreview] = useState(false);
  const isEditing = recordId !== null;
  const [saving, setSaving] = React.useState(false);
  const [initialData, setInitialData] =
    useState<SupplierOrderDataEntryForm | null>(null);
  const [headerVersion, setHeaderVersion] = useState(0);

  // Define our useForm and must be declared before any UseEffects
  const form = useForm<SupplierOrderDataEntryForm>({
    initialValues: initialData ?? {
      id: 0 /**not used but required because of the BaseEntity */,
      summary: {
        id: 0,
        supplierId: 0,
        createdOn: getLocalDateString(),
        supplierName: "",
        exchange: 0,
        commission: 0,
        pr1Percent: 0,
        pr2Percent: 0,
        freight: 0,
        notes: "",
        retailMargin: 0,
        totalVolume: 0,
        isProcessed: false,
        isPricesPrinted: false,
        isCostingPrinted: false,
        isPrinted: false,
        isOrdersReceived: false,
      },
      entries: [],
    },
  });
  const { summary } = form.getValues();
  /** Run one and fetch teh supplier.  There's only < dozen suppliers */
  useEffect(() => {
    let mounted = true;
    const fetchSuppliers = async () => {
      const response = await safeApiCall(getSuppliers());
      const mapped = response.data
        .map((s: { id: number; supplierName: string }) => ({
          value: s.id.toString(),
          label: s.supplierName,
        }))
        .sort(
          (
            a: { value: number; label: string },
            b: { value: number; label: string },
          ) => a.label.localeCompare(b.label),
        ); // sort by label
      if (!mounted) return;

      setSuppliers(mapped);
    };

    fetchSuppliers(); // only dozen suppliers, no need for pagination or search

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!recordId) return;
    //if (apiFetchRef.current) return;

    //apiFetchRef.current = true;
    let mounted = true;

    const fetchInitialData = async () => {
      try {
        const response = await safeApiCall(getSupplierOrderById(recordId));
        if (!mounted) return;

        form.setValues({
          ...form.values,
          summary: response.summary,
        });
        handlers.setState(response.entries);
      } catch (error) {
        logError(error);
      }
    };
    fetchInitialData();
    // what this protects
    // Can’t perform a React state update on an unmounted component.
    // if component is unmounted and we're setting state becuase of
    // api calls that's async etc...
    return () => {
      mounted = false;
    };
  }, [recordId]); // monitors recordId..but will only retrigger if the id changes.. null > value > different value etc

  const handleRemoveEntry = async (index: number) => {
    const entry = entries[index]; // form.values.entries[index];
    const entryId = Number(entry.id); // coerce to number safely
    const productId = String(entry.productId); // ensure string for comparison

    // Confirm removal if entry has a valid ID
    if (!isNaN(entryId) && entryId > 0) {
      try {
        await safeApiCall(
          deleteSupplierOrderDetailsById(Number(summary.id), entryId),
        );
        handlers.remove(index);
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
      } catch (error) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
          message: MSG_DEFAULT_FORM_SUBMIT_ERR,
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
        logError(error);
      }
    } else {
      handlers.remove(index); // newly added entry, just remove locally
    }
  };
  const handleUpdateEntry = (index: number, updated: SupplierOrderEntry) => {
    // Lookup product only if productId has changed
    const product = allProducts.items.find(
      (p) => Number(p.productCode) === updated.productId,
    );
    if (product) {
      const productDesc = `${product.title} (${product.unitsCtn} CTN)`;
      handlers.setItem(index, {
        ...updated,
        productCode: "C",
        productDescription: productDesc,
        quantity: updated.quantity,
        actual: updated.actual,
        supplierOrderId: updated.supplierOrderId ?? 0,
        barcode: product.barcode,
        altCode: product.altCode,
        bestBeforeDate: updated.bestBeforeDate ?? "",
        supplierBarcode: updated.supplierBarcode,
        commission: updated.commission ?? 0,
        pr1Percent: updated.pr1Percent ?? 0,
        pr2Percent: updated.pr2Percent ?? 0,
        newCostPrice: updated.newCostPrice ?? 0,
        newSellPrice1: updated.newSellPrice1 ?? 0,
        newSellPrice2: updated.newSellPrice2 ?? 0,
        oldSellPrice1: product.sellPrice1,
        oldSellPrice2: product.sellPrice2,
      });
    } else {
      handlers.setItem(index, {
        ...updated,
        productDescription: "Unknown Product",
      });
    }
  };

  // mark as printed
  const handlePrint = async (type: "altCode" | "noAltCode" | "summary") => {
    const { summary } = form.getValues();
    form.setFieldValue("summary.isPrinted", true);
    await safeApiCall(togglePrintState(Number(summary.id), "summary"));

    // force save first
    handleSubmit(false, true);

    //setPreviewType(type);
    setTimeout(() => {
      navigateTo(`print/supplier-order-alt?id=${summary.id}&t=${type}&e=1`);
    }, 500);

    //setPrint(true);
  };
  const handlePrintCosting = async () => {
    const { summary } = form.getValues();
    form.setFieldValue("summary.isCostingPrinted", true);

    await safeApiCall(togglePrintState(Number(recordId), "costing"));
    // force save first
    handleSubmit(false, true);

    setTimeout(() => {
      navigateTo(`print/supplier-order?id=${summary.id}&t=costing&e=1`);
    }, 500);
  };
  const handlePrintPricing = async () => {
    const { summary } = form.getValues();

    form.setFieldValue("summary.isPricesPrinted", true);
    await safeApiCall(togglePrintState(Number(summary.id), "prices"));
    // force save first
    handleSubmit(false, true);
    if (!summary.isPricesPrinted) {
      await safeApiCall(calculateSupplierOrderPricing(Number(summary.id)));
    }

    setTimeout(() => {
      navigateTo(`print/supplier-order?id=${summary.id}&t=prices&e=1`);
    }, 500);
  };
  const handleResetPrices = async () => {
    await safeApiCall(calculateSupplierOrderPricing(Number(summary.id)));
    setTimeout(() => {
      navigateTo(`print/supplier-order?id=${summary.id}&t=prices&e=1`);
    }, 1000);
  };

  // const knownProductIds = new Set(
  //   allProducts.items
  //     .map((p) => Number(p?.productCode)) // normalize to number
  //     .filter((id) => !isNaN(id)), // keep only valid numbers
  // );

  //const seenProductIds = new Set<number>();

  // controls the disabled state of the Process button
  const canProcess = (): boolean => {
    const { summary } = form.getValues();

    if (summary.isProcessed) {
      return false; // negate - already processed
    }

    // Otherwise, disallow processing if all slips are not printed,
    // or if customer is inactive, or if saving
    return (
      summary.isPricesPrinted && summary.isPrinted && summary.isCostingPrinted
    );
  };

  /**
   * Marks the order as received.  This will set the isOrdersReceived flag to true and trigger a remount of the rows to reset their internal state.  Once an order is marked as received, the entry rows will show additional controls for commission and price changes, and the product code field will be locked as we need to preserve the original product for accurate receiving and inventory updates.
   */
  const handleOrderReceived = async () => {
    form.setFieldValue("summary.isOrdersReceived", true);
    // if we have order entries, we need to update the headerVersion to force the header to remount and reset the internal state of the rows
    if (entries.length > 0) {
      setHeaderVersion((v) => v + 1); // force remount of rows to reset internal state
    }
    await safeApiCall(orderReceived(Number(recordId)));
  };
  /**
   * Process the credit note.  This works by setting the isProcessed flag to true
   * and then calling handleSubmit to save the form.  The backend will handle the rest.
   * @returns
   */
  const handleProcess = async () => {
    if (canProcess() === false) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "Order already processed or you haven't printed all reports",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }
    if (entries.length === 0) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "Nothing to process.  Order has no line items",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    const updatedValues = {
      ...form.getValues(),
      summary: {
        ...form.getValues().summary,
        isProcessed: true,
      },
    };
    form.setValues(updatedValues);

    // Let React commit state before submitting
    setTimeout(() => {
      handleSubmit(true, false);
    }, 0);
  };
  /**
   * Save the form data to the backend.
   * @param close If true, close the form after saving
   * @returns
   */
  const handleSubmit = async (close = false, silent = false) => {
    if (apiPostRef.current) return; // already submitting
    if (form.validate().hasErrors) return;

    setError(null);
    const formData: SupplierOrderDataEntryForm = form.getValues();

    // if silent = true, but processed already... don't save again
    if (silent && formData.summary.isProcessed) {
      return;
    }

    if (formData.summary.supplierId <= 0) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_INVALID,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    // form is invalid.
    setSaving(true);

    let result: any;
    try {
      //inject the input values into the payl oad
      //const payload: SupplierOrderDataEntryForm = form.getValues();
      // inject the input values into the payl oad
      const payload: SupplierOrderDataEntryForm = {
        ...formData,
        entries,
      };
      apiPostRef.current = true;
      result = await safeApiCall(submitSupplierOrder(payload));
      apiPostRef.current = false;

      if (!result?.entries || !Array.isArray(result.entries)) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: "Invalid response: entries is missing or not an array.",
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });

        return;
      }

      if (result?.statusText === "Internal Server Error") {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: result?.statusText,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });

        return;
      }

      startTransition(() => {
        const formData = form.getValues();
        form.setValues({
          id: result.orderId,
          summary: {
            ...formData.summary,
            id: result.orderId,
          },
        });
        handlers.setState(result.entries);
      });

      if (!silent) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Success,
        });
      }

      close && onSaveComplete(close);
    } catch (error) {
      apiPostRef.current = false;
      logError(error);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      if (result && result.error) {
        setError(JSON.stringify(result.error));
      }
    } finally {
      // done
      setSaving(false);
    }
  };
  const handleResetEntry = async () => {
    if (confirmReset() === false) return;

    await safeApiCall(resetSupplierOrderDetails(Number(summary.id)));
    handlers.setState([]);
  };

  const handleAddEntry = () => {
    handlers.append({
      id: 0,
      supplierOrderId: Number(recordId),
      productId: 0,
      productCode: "",
      productDescription: "",
      quantity: 0,
      actual: 0,
      barcode: "",
      altCode: "",
      bestBeforeDate: "",
      supplierBarcode: "",
      commission: 0,
      pr1Percent: 0,
      pr2Percent: 0,
      newSellPrice1: 0,
      newSellPrice2: 0,
      isBackOrderItem: false,
      oldSellPrice1: 0,
      oldSellPrice2: 0,
      oldRetail: 0,
      newRetail: 0,
      oldCostPrice: 0,
      newCostPrice: 0,
    });

    // focus the new row after render
    // double wrap of requestAnimationFrame: This prevents the resize → re-render → resize loop.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const lastIndex = entries.length; // because append adds one
        const el = document.querySelector(
          `[data-row-index="${lastIndex}"] input`,
        ) as HTMLInputElement;
        el?.focus();
      });
    });
  };

  const handleDownloadCsv = () => {
    const { summary } = form.getValues();
    if (!summary) return;

    const summaryRow = {
      orderId: summary.id,
      createdOn: formatDate(summary.createdOn ?? ""),
      totalVolume: summary.totalVolume,
      supplierName: summary.supplierName,
      exchange: summary.exchange,
      commission: summary.commission,
      freight: summary.freight,
      pr1Percent: summary.pr1Percent,
      pr2Percent: summary.pr2Percent,
      retailMargin: summary.retailMargin,
      notes: summary.notes,
    };

    const CSV_FIELDS: (keyof SupplierOrderEntry)[] = [
      "productId",
      "productCode",
      "altCode",
      "productDescription",
      "quantity",
      "actual", // ordered
      "pr1Percent",
      "oldCostPrice",
      "newCostPrice",
      "oldSellPrice1",
      "newSellPrice1",
      "oldSellPrice2",
      "newSellPrice2",
      "bestBeforeDate",
      "priceChange",
    ];

    const detailRows = pickFields(entries ?? [], CSV_FIELDS);
    const summaryCsv = toCsv([summaryRow]);
    const csv = toCsv(detailRows);

    const blob = new Blob([`${summaryCsv}\n\n${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    //const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `supplier-order-${summary.id}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <Title order={3}>Supplier Order Entry</Title>

        <Button
          variant="outline"
          size="xs"
          disabled={saving || summary.id === 0}
          leftSection={<IconDownload size={14} />}
          onClick={() => {
            handleDownloadCsv();
          }}
        >
          Download CSV
        </Button>
      </Group>

      <Group grow align="flex-start">
        <TextInput
          label="Order ID"
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={summary.id === 0 ? "New" : summary.id}
          disabled
        />
        <TextInput
          label="Created On"
          value={formatDate(summary.createdOn)}
          disabled
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <TextInput
          label="Volume (calculated)"
          value={formatNumberAsCurrencyString(summary.totalVolume, 0, 3)}
          readOnly
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <Select
          label="Supplier"
          placeholder="Select a supplier"
          clearable
          searchable
          data={suppliers}
          {...form.getInputProps("summary.supplierId")}
          onChange={(v) => form.setFieldValue("summary.supplierId", Number(v))}
          value={summary.supplierId?.toString()}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
        />
      </Group>

      <Group grow align="flex-start">
        <NumberInput
          label="XChange"
          {...form.getInputProps("summary.exchange")}
          maxLength={5}
          disabled={!summary.isOrdersReceived}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="Comm %"
          {...form.getInputProps("summary.commission")}
          maxLength={5}
          disabled={!summary.isOrdersReceived}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="Freight"
          {...form.getInputProps("summary.freight")}
          maxLength={9}
          disabled={!summary.isOrdersReceived}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="PR1 %"
          {...form.getInputProps("summary.pr1Percent")}
          maxLength={5}
          disabled={!summary.isOrdersReceived}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="PR2 %"
          {...form.getInputProps("summary.pr2Percent")}
          maxLength={5}
          disabled={!summary.isOrdersReceived}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="Retail Margin"
          {...form.getInputProps("summary.retailMargin")}
          maxLength={5}
          disabled={!summary.isOrdersReceived}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
      </Group>
      <Group grow align="flex-start">
        <Textarea
          label="Notes"
          placeholder="Enter notes"
          maxLength={1000}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          {...form.getInputProps("summary.notes")}
          mt="md"
        />
      </Group>
      <input type="hidden" {...form.getInputProps("summary.supplierId")} />

      <Divider
        my="md"
        label={`Order Entries (${entries.length})`}
        labelPosition="center"
      />
      <Title order={3}>Order Entries</Title>
      <Group justify="space-between" mt="md">
        <Group>
          <Button
            variant="light"
            onClick={handleAddEntry}
            disabled={saving || summary.isProcessed || summary.id === 0}
            leftSection={<IconPlus size={16} />}
          >
            Add Entry
          </Button>
          <Button
            variant="light"
            onClick={handleResetEntry}
            disabled={saving || summary.isProcessed || summary.id === 0}
            leftSection={<IconPlus size={16} />}
            title="This reset will delete all entries and is non reversible"
          >
            Reset Order Details
          </Button>
          <Button
            variant="light"
            onClick={handleResetPrices}
            disabled={saving || !summary.isOrdersReceived || summary.id === 0}
            leftSection={<IconPlus size={16} />}
            title="This reset will delete all entries and is non reversible"
          >
            Reset Prices
          </Button>
        </Group>
        <Group>
          <Button
            onClick={() => handleSubmit()}
            title="Save order. This does not process the order."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || summary.isProcessed}
          >
            Save
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            title="Save order and close. This does not process the order."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || summary.isProcessed}
          >
            Save & Close
          </Button>

          {Number(summary.id) > 0 && (
            <>
              <Button
                onClick={() => handlePrint("altCode")}
                disabled={!isEditing || saving}
                leftSection={<IconPrinter size={16} />}
                rightSection={
                  <IconCheck
                    color={`${summary.isPrinted ? "white" : "gray"}`}
                    size={16}
                  />
                }
              >
                ALT Code
              </Button>
              <Button
                onClick={() => handlePrint("noAltCode")}
                disabled={!isEditing || saving}
                leftSection={<IconPrinter size={16} />}
                rightSection={
                  <IconCheck
                    color={`${summary.isPrinted ? "white" : "gray"}`}
                    size={16}
                  />
                }
              >
                w/o ALT Code
              </Button>
              <Button
                onClick={() => handlePrint("summary")}
                disabled={!isEditing || saving}
                leftSection={<IconPrinter size={16} />}
                rightSection={
                  <IconCheck
                    color={`${summary.isPrinted ? "white" : "gray"}`}
                    size={16}
                  />
                }
              >
                Summary
              </Button>
            </>
          )}

          {Number(summary.id) > 0 && summary.isPrinted && (
            <>
              <Button
                onClick={handlePrintPricing}
                disabled={saving}
                leftSection={<IconPrinter size={16} />}
                rightSection={
                  <IconCheck
                    color={`${summary.isPricesPrinted ? "white" : "gray"}`}
                    size={16}
                  />
                }
              >
                Pricing
              </Button>
              <Button
                onClick={handlePrintCosting}
                disabled={saving}
                leftSection={<IconPrinter size={16} />}
                rightSection={
                  <IconCheck
                    color={`${summary.isCostingPrinted ? "white" : "gray"}`}
                    size={16}
                  />
                }
              >
                Costing
              </Button>
              <Button
                title="Order received. This action cannot be undone."
                onClick={handleOrderReceived}
                disabled={!isEditing || summary.isOrdersReceived || saving}
                leftSection={<IconProgressCheck size={16} />}
                rightSection={
                  <IconCheck
                    color={`${summary.isOrdersReceived ? "white" : "gray"}`}
                    size={16}
                  />
                }
              >
                Received
              </Button>
              <Button
                title="Process supplier order. This action cannot be un-done."
                onClick={handleProcess}
                disabled={
                  entries.length === 0 ||
                  !summary.isOrdersReceived ||
                  summary.isProcessed
                }
                leftSection={<IconProgressCheck size={16} />}
                rightSection={
                  <IconCheck
                    color={`${summary.isProcessed ? "green" : "gray"}`}
                    size={16}
                  />
                }
              >
                Process Order
              </Button>
            </>
          )}
        </Group>
      </Group>
      {error && (
        <Alert title="Error" color="red">
          {error}
        </Alert>
      )}
      {entries.length > 0 && (
        <Stack gap="sm" className="form">
          <Virtuoso
            style={{ height: 650, overflowY: "auto", contain: "layout" }}
            totalCount={entries.length}
            increaseViewportBy={{ top: 200, bottom: 200 }}
            overscan={300}
            defaultItemHeight={48} // ← stabilizes measurement
            components={{
              Header: () => (
                <OrderEntryHeaderRow
                  key={headerVersion} // ← correct place to use headerVersion
                  orderReceived={summary.isOrdersReceived}
                  onCommChange={(v) => {
                    setHeaderVersion((v) => v + 1); // force remount of rows to reset internal state
                    handlers.apply((entry) => ({
                      ...entry,
                      commission: v ?? 0,
                    }));
                  }}
                  onPr1Change={(v) => {
                    setHeaderVersion((v) => v + 1); // force remount of rows to reset internal state
                    handlers.apply((entry) => ({
                      ...entry,
                      pr1Percent: v ?? 0,
                    }));
                  }}
                />
              ),
            }}
            itemContent={(index) => {
              const entry = entries[index];
              return (
                <OrderEntryRow
                  key={`${entry.id}-${index}-${headerVersion}`} // force remount when id changes
                  entry={entry}
                  index={index}
                  orderReceived={summary.isOrdersReceived}
                  isLastRow={index === entries.length - 1}
                  onNewEntry={handleAddEntry}
                  onUpdate={(updated) => handleUpdateEntry(index, updated)}
                  onRemove={() => handleRemoveEntry(index)}
                />
              );
            }}
          />
          <div className="spacer padTop padBottom" />
          <div className="spacer padTop padBottom" />
        </Stack>
      )}

      <Modal
        opened={saving}
        onClose={() => setSaving(false)}
        withCloseButton={false}
        centered
        zIndex={3000}
        overlayProps={{ opacity: 0.3, blur: 2 }}
      >
        <Stack align="center" justify="center">
          <Loader />
          <Text fw={500} ta="center">
            Saving data...
            <br />
            Please do not close or refresh the screen.
          </Text>
        </Stack>
      </Modal>
      <Modal
        opened={preview}
        onClose={() => setPreview(false)}
        fullScreen
        title="Order Preview"
        size="xl"
        centered
        closeOnEscape={false}
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        {recordId && (
          <AdminSupplierOrderPreview recordId={recordId} /> //  {"we pass pritnOrderStage instead of summary.orderStage. the latter represents the actual state in the backend"}
        )}
      </Modal>
    </Stack>
  );
};

export default AdminSupplierOrderEntryPage;
